import React, { useEffect, useRef, useState } from "react";
import { Route } from "lucide-react";
import { useTranslation } from "react-i18next";
import { get_recording_summaries } from "../../api";
import type { Recording, RecordingSummary } from "../../api/types";
import AppUsagePreview from "../AppUsagePreview";

/** The server's cap on one summaries request. */
const MAX_BATCH = 100;
type Waiter = {
  id: string;
  resolve: (summary: RecordingSummary | undefined) => void;
  reject: (error: unknown) => void;
};
let waiting: Waiter[] = [];

const flushSummaries = () => {
  const batch = waiting;
  waiting = [];
  for (let start = 0; start < batch.length; start += MAX_BATCH) {
    const chunk = batch.slice(start, start + MAX_BATCH);
    get_recording_summaries([...new Set(chunk.map(waiter => waiter.id))]).then(
      summaries => {
        const byId = new Map(summaries.map(summary => [summary.recording_id, summary]));
        for (const waiter of chunk) waiter.resolve(byId.get(waiter.id));
      },
      error => {
        for (const waiter of chunk) waiter.reject(error);
      },
    );
  }
};

/** Rows ask as they near the viewport; one request answers every row that asked in the same task. */
const loadSummary = (id: string) =>
  new Promise<RecordingSummary | undefined>((resolve, reject) => {
    if (!waiting.length) setTimeout(flushSummaries);
    waiting.push({ id, resolve, reject });
  });

/** A session row's top apps and process count, loaded once the row nears the viewport. */
export default function SessionApps({
  recording,
  showProcessCount = false,
  alwaysVisible = false,
  revision = 0,
}: {
  recording: Recording;
  showProcessCount?: boolean;
  alwaysVisible?: boolean;
  revision?: number;
}) {
  const { t } = useTranslation();
  const element = useRef<HTMLDivElement>(null);
  const [summary, setSummary] = useState<RecordingSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const status = recording.status.status;
  const rerun = recording.rerun?.status;
  useEffect(() => {
    setSummary(null);
    setFailed(false);
    if (status !== "completed") return;
    let cancelled = false;
    const load = () =>
      loadSummary(recording.id).then(
        loaded => {
          if (cancelled) return;
          if (loaded) setSummary(loaded);
          else setFailed(true);
        },
        () => {
          if (!cancelled) setFailed(true);
        },
      );
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver === "undefined") void load();
    else {
      observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            observer?.disconnect();
            void load();
          }
        },
        { rootMargin: "200px" },
      );
      if (element.current) observer.observe(element.current);
    }
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [recording.id, status, rerun, revision]);
  const appSeconds = summary?.app_seconds ?? {};
  const apps = Object.keys(appSeconds).sort((a, b) => appSeconds[b] - appSeconds[a]);
  const processCount = summary?.process_count ?? null;
  const unavailable = t(
    status === "completed" && !failed
      ? "activity.loading_process_count"
      : "activity.process_count_unavailable",
  );
  return (
    <>
      <div
        ref={showProcessCount ? undefined : element}
        className={`relative z-10 min-h-8 w-[7.75rem] shrink-0 items-center gap-2 ${alwaysVisible ? "flex" : "hidden @[960px]:flex"}`}
      >
        {apps.length > 0 && (
          <AppUsagePreview apps={apps} appSeconds={appSeconds} label={t("activity.top_apps")} />
        )}
      </div>
      {showProcessCount && (
        <div
          ref={element}
          className={`w-28 shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-text-dim tabular-nums ${alwaysVisible ? "flex" : "hidden @[800px]:flex"}`}
          title={processCount === null ? unavailable : t("activity.processes_detected")}
        >
          <Route size={14} aria-hidden="true" />
          {processCount === null ? (
            <span aria-label={unavailable}>{status === "completed" && !failed ? "…" : "—"}</span>
          ) : (
            t("n_processes", { count: processCount })
          )}
        </div>
      )}
    </>
  );
}
